/***
 * File name: index.js
 *
 * Program Description:
 * This file contains all the meta configuration of the Personal Data Breach
 * Notification Form
 *
 * This file defines the ff:
 * - Tabs within the form
 * - Schema of the collection
 * - Form parameters for the file upload
 * - Field validations for all the tabs
 * - The start index to indicate which tab would be shown on mount of the component
 * - Save index to indicate which tab would show the save button
 * - Tab names, id and index
 * - Import of meta config for each tab indicating the input fields
 * - Fields that needs to be watched
 * - Fields that needs to be converted to object ids before saving
 *
 */
import { custom } from '@/utils/validations'
import type from './tabs/type'
import details from './tabs/details'
import implementation from './tabs/implementation'

export default function (vm) {
  const filteredNotif = vm.formItem.notificationType.filter(notif => notif.checkbox === true)
  const hideImplementaion = () => {
    if (vm.isPic) return vm.status !== 'Initial Order'
    return vm.status === 'create' || vm.status === 'Drafted' || vm.status === 'Submitted'
  }
  const notificationType = filteredNotif.map(notif => {
    return {
      value: notif._id,
      remarks: notif.remarks,
      subtype: notif.subtype
    }
  })
  const specificCause = vm.formItem.specificCause
  const isSpecificCauseOthers = specificCause === 'Others' || specificCause === ''

  let validations = {}
  if (vm.status === 'create' || vm.status === 'Drafted' || vm.status === 'Submitted') {
    validations = custom.pdbnf(vm)
  } else if (vm.status === 'For Evaluation' || vm.status === 'Initial Review' || vm.status === 'Final Review') {
    validations = custom.pdbnfEvaluate(vm)
  } else if (vm.status === 'Initial Order') {
    validations = custom.pdbnfInitialOrder(vm)
  }

  return {
    label: 'Personal Data Breach Notification Form',
    allCapsHeader: false,
    showConfirmDialog: true,
    deleteDisable: false,
    form: {
      createReroute: 'DataBreachView',
      backRoute: { name: 'DataBreachTable' },
      schema: {
        pdbnf: {
          pic: vm.formItem.pic,
          status: vm.formItem.status,
          representative: vm.formItem.representative,
          briefSummary: vm.formItem.briefSummary,
          contactInfo: vm.formItem.contactInfo,
          dateOfOccurrence: vm.formItem.dateOfOccurrence,
          dateOfDiscovery: vm.formItem.dateOfDiscovery,
          dateOfNotification: vm.formItem.dateOfNotification,
          notificationType,
          notification: vm.formItem.notification,
          generalCause: vm.formItem.generalCause === '' ? null : vm.formItem.generalCause,
          specificCause: isSpecificCauseOthers ? null : specificCause,
          specificCauseOthers: vm.formItem.specificCauseOthers === '' ? null : vm.formItem.specificCauseOthers,
          withRequest: vm.formItem.withRequest,
          typeOfRequest: vm.formItem.typeOfRequest,
          requestExtension: vm.changeReqData.oldRequestExtension ? vm.formItem.requestExtension + '\n\n' + vm.changeReqData.oldRequestExtension : vm.formItem.requestExtension,
          justificationExemption: vm.formItem.justificationExemption,
          justificationPostponement: vm.formItem.justificationPostponement,
          justificationAlternative: vm.formItem.justificationAlternative,
          justificationOthers: vm.formItem.justificationOthers,
          extensionDays: vm.formItem.extensionDays,
          extensionStart: vm.formItem.extensionStart,
          extensionEnd: vm.formItem.extensionEnd,
          numberOfDataSubjectsAffected: vm.formItem.numberOfDataSubjectsAffected,
          atLeastOneHundredIndividuals: vm.formItem.atLeastOneHundredIndividuals,
          typeOfRequestOther: vm.formItem.typeOfRequestOther,
          typeOfRequestOtherInitialOrder: vm.formItem.typeOfRequestOtherInitialOrder,
          howBreachOccurred: {
            details: vm.formItem.howBreachOccurred,
            sufficientlyProvided: vm.formItem.howBreachOccurredProvided,
            remarks: vm.formItem.howBreachOccurredRemarks
          },
          chronology: {
            details: vm.formItem.chronology,
            sufficientlyProvided: vm.formItem.chronologyProvided,
            remarks: vm.formItem.chronologyRemarks
          },
          numberOfDataSubjects: {
            details: vm.formItem.numberOfDataSubjects,
            sufficientlyProvided: vm.formItem.numberOfDataSubjectsProvided,
            remarks: vm.formItem.numberOfDataSubjectsRemarks
          },
          descriptionOrNature: {
            details: vm.formItem.descriptionOrNature,
            sufficientlyProvided: vm.formItem.descriptionOrNatureProvided,
            remarks: vm.formItem.descriptionOrNatureRemarks
          },
          likelyConsequences: {
            details: vm.formItem.likelyConsequences,
            sufficientlyProvided: vm.formItem.likelyConsequencesProvided,
            remarks: vm.formItem.likelyConsequencesRemarks
          },
          dataProtectionOfficer: {
            details: vm.formItem.dataProtectionOfficer,
            sufficientlyProvided: vm.formItem.dataProtectionOfficerProvided,
            remarks: vm.formItem.dataProtectionOfficerRemarks
          },
          sensitivePersonalInformation: {
            details: vm.formItem.sensitivePersonalInformation,
            sufficientlyProvided: vm.formItem.sensitivePersonalInformationProvided,
            remarks: vm.formItem.sensitivePersonalInformationRemarks
          },
          identityFraud: {
            details: vm.formItem.identityFraud,
            sufficientlyProvided: vm.formItem.identityFraudProvided,
            remarks: vm.formItem.identityFraudRemarks
          },
          measuresToAddress: {
            details: vm.formItem.measuresToAddress,
            sufficientlyProvided: vm.formItem.measuresToAddressProvided,
            remarks: vm.formItem.measuresToAddressRemarks
          },
          measuresToSecure: {
            details: vm.formItem.measuresToSecure,
            sufficientlyProvided: vm.formItem.measuresToSecureProvided,
            remarks: vm.formItem.measuresToSecureRemarks
          },
          actionsToMitigateHarm: {
            details: vm.formItem.actionsToMitigateHarm,
            sufficientlyProvided: vm.formItem.actionsToMitigateHarmProvided,
            remarks: vm.formItem.actionsToMitigateHarmRemarks
          },
          actionsToInformDataSubjects: {
            details: vm.formItem.actionsToInformDataSubjects,
            sufficientlyProvided: vm.formItem.actionsToInformDataSubjectsProvided,
            remarks: vm.formItem.actionsToInformDataSubjectsRemarks
          },
          measuresToPreventRecurrence: {
            details: vm.formItem.measuresToPreventRecurrence,
            sufficientlyProvided: vm.formItem.measuresToPreventRecurrenceProvided,
            remarks: vm.formItem.measuresToPreventRecurrenceRemarks
          },
          recordType: vm.formItem.recordType,
          dataSubjects: vm.formItem.dataSubjects,
          specifyDataSubjectsOthers: vm.formItem.specifyDataSubjectsOthers,
          specifyDataSubjectsVulnerable: vm.formItem.specifyDataSubjectsVulnerable
        }
      },
      evaluateSchema: {
        justified: vm.formItem.justified,
        possibleViolation: vm.formItem.possibleViolation,
        numberOfDataSubjectsAffected: vm.formItem.numberOfDataSubjectsAffected,
        atLeastOneHundredIndividuals: vm.formItem.atLeastOneHundredIndividuals,
        dataSubjects: vm.formItem.dataSubjects,
        specifyDataSubjectsOthers: vm.formItem.specifyDataSubjectsOthers,
        specifyDataSubjectsVulnerable: vm.formItem.specifyDataSubjectsVulnerable,
        notification: vm.formItem.notification,
        howBreachOccurred: {
          sufficientlyProvided: vm.formItem.howBreachOccurredProvided,
          remarks: vm.formItem.howBreachOccurredRemarks
        },
        chronology: {
          sufficientlyProvided: vm.formItem.chronologyProvided,
          remarks: vm.formItem.chronologyRemarks
        },
        numberOfDataSubjects: {
          sufficientlyProvided: vm.formItem.numberOfDataSubjectsProvided,
          remarks: vm.formItem.numberOfDataSubjectsRemarks
        },
        descriptionOrNature: {
          sufficientlyProvided: vm.formItem.descriptionOrNatureProvided,
          remarks: vm.formItem.descriptionOrNatureRemarks
        },
        likelyConsequences: {
          sufficientlyProvided: vm.formItem.likelyConsequencesProvided,
          remarks: vm.formItem.likelyConsequencesRemarks
        },
        dataProtectionOfficer: {
          sufficientlyProvided: vm.formItem.dataProtectionOfficerProvided,
          remarks: vm.formItem.dataProtectionOfficerRemarks
        },
        sensitivePersonalInformation: {
          sufficientlyProvided: vm.formItem.sensitivePersonalInformationProvided,
          remarks: vm.formItem.sensitivePersonalInformationRemarks
        },
        identityFraud: {
          sufficientlyProvided: vm.formItem.identityFraudProvided,
          remarks: vm.formItem.identityFraudRemarks
        },
        measuresToAddress: {
          sufficientlyProvided: vm.formItem.measuresToAddressProvided,
          remarks: vm.formItem.measuresToAddressRemarks
        },
        measuresToSecure: {
          sufficientlyProvided: vm.formItem.measuresToSecureProvided,
          remarks: vm.formItem.measuresToSecureRemarks
        },
        actionsToMitigateHarm: {
          sufficientlyProvided: vm.formItem.actionsToMitigateHarmProvided,
          remarks: vm.formItem.actionsToMitigateHarmRemarks
        },
        actionsToInformDataSubjects: {
          sufficientlyProvided: vm.formItem.actionsToInformDataSubjectsProvided,
          remarks: vm.formItem.actionsToInformDataSubjectsRemarks
        },
        measuresToPreventRecurrence: {
          sufficientlyProvided: vm.formItem.measuresToPreventRecurrenceProvided,
          remarks: vm.formItem.measuresToPreventRecurrenceRemarks
        },
        enabled: {
          fullReport: vm.formItem.fullReportCheckbox,
          privacyImpactAssessment: vm.formItem.privacyImpactAssessmentCheckbox,
          securityIncidentManagementPolicy: vm.formItem.securityIncidentManagementPolicyCheckbox,
          operationsSecurity: vm.formItem.operationsSecurityCheckbox,
          systemAcquisition: vm.formItem.systemAcquisitionCheckbox,
          accessControl: vm.formItem.accessControlCheckbox,
          communicationsSecurity: vm.formItem.communicationsSecurityCheckbox,
          physicalAndEnvironmentalSecurity: vm.formItem.physicalAndEnvironmentalSecurityCheckbox,
          cryptography: vm.formItem.cryptographyCheckbox,
          assetManagement: vm.formItem.assetManagementCheckbox,
          cryptograhpy: vm.formItem.cryptograhpyCheckbox,
          humanResourceSecurity: vm.formItem.humanResourceSecurityCheckbox,
          compliance: vm.formItem.complianceCheckbox,
          informationSecurityPolicy: vm.formItem.informationSecurityPolicyCheckbox,
          dataSubjectNotification: vm.formItem.dataSubjectNotificationCheckbox,
          delayInNotificationExplanation: vm.formItem.delayInNotificationExplanationCheckbox,
          othersAttachment: vm.formItem.othersAttachmentCheckbox
        },
        others: vm.formItem.others,
        daysForDocumentSubmission: vm.formItem.daysForDocumentSubmission,
        notificationType,
        recordType: vm.formItem.recordType
      },
      initialOrderSchema: {
        hasOthers: vm.formItem.hasOthers,
        others: vm.formItem.others
      },
      formParams: {
        query: {},
        fields: {
          fullReport: vm.formItem.fullReportFile,
          privacyImpactAssessment: vm.formItem.privacyImpactAssessmentFile,
          securityIncidentManagementPolicy: vm.formItem.securityIncidentManagementPolicyFile,
          operationsSecurity: vm.formItem.operationsSecurityFile,
          systemAcquisition: vm.formItem.systemAcquisitionFile,
          accessControl: vm.formItem.accessControlFile,
          communicationsSecurity: vm.formItem.communicationsSecurityFile,
          physicalAndEnvironmentalSecurity: vm.formItem.physicalAndEnvironmentalSecurityFile,
          assetManagement: vm.formItem.assetManagementFile,
          cryptography: vm.formItem.cryptographyFile,
          humanResourceSecurity: vm.formItem.humanResourceSecurityFile,
          compliance: vm.formItem.complianceFile,
          informationSecurityPolicy: vm.formItem.informationSecurityPolicyFile,
          dataSubjectNotification: vm.formItem.dataSubjectNotificationFile,
          delayInNotificationExplanation: vm.formItem.delayInNotificationExplanationFile,
          othersAttachment: vm.formItem.othersAttachmentFile
        }
      },
      validations: {
        pdbnf: validations
      },
      startIndex: {
        pdbnf: 1
      },
      saveIndex: {
        pdbnf: hideImplementaion() ? 2 : 3
      },
      tabs: {
        pdbnf: hideImplementaion()
          ? [
            { index: 0, id: 'NT', name: 'Notification Type' },
            { index: 1, id: 'ND', name: 'Data Breach Notification Details' }
          ]
          : [
            { index: 0, id: 'NT', name: 'Notification Type' },
            { index: 1, id: 'ND', name: 'Data Breach Notification Details' },
            { index: 2, id: 'ISC', name: 'Implementation of Security Measures / Controls' }
          ]
      },
      fields: {
        pdbnf: hideImplementaion()
          ? [
            type(vm),
            details(vm)
          ]
          : [
            type(vm),
            details(vm),
            implementation(vm)
          ]
      },
      watchFields: {
      },
      convertToObjId: {
        pdbnf: ['recordType']
      }
    }
  }
}
